import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowSquareOut, BookmarkSimple, Check, X } from "@phosphor-icons/react";
import type { AppView, AppViewStatus, Decision, Job, JobDetail } from "../../shared/types.ts";
import { APP_STATUS, AppStatusBadge, Button, IconButton, Monogram, Sheet, SourceLogo, StatusBadge, ToneDot, cn, rise, staggerParent } from "../ui/index.ts";
import { errorText, getJob, retry, webUrl } from "../lib/api.ts";
import { changed, useApp } from "../lib/ctx.ts";
import { ago, blocks, FAMILIES, list, pay, place, places, SENIORITY, utc, visaNote, WORKPLACE } from "../lib/format.ts";
import { FitPanel } from "./Fit.tsx";
import { applyLabel, ghostRisk, Loading, MatchChip, SendButton, swap, useDecide } from "./parts.tsx";

/** Statuses past the point of undo, as the apply button reads once there. */
const SENT_LABEL: Partial<Record<AppViewStatus, string>> = {
  applying: "Sending",
  submitted: "Applied",
  confirmed: "Confirmed",
  on_site: "Applied on site",
};

/**
 * A job in a sheet. `onAct` hands decisions to the caller (the Today feed animates its card out);
 * without it the body records them itself. `onApplied` hears about applies the body sent or took back itself.
 * `short` clamps the description to a few lines with a Show full toggle.
 */
export function Detail({
  job,
  onClose,
  onAct,
  onApplied,
  mark,
  short = false,
}: {
  job: Job | null;
  onClose: () => void;
  onAct?: (d: Decision, job: Job) => void;
  onApplied?: (job: Job, on: boolean) => void;
  mark?: Decision | null;
  short?: boolean;
}) {
  const last = useRef<Job | null>(job);
  if (job) last.current = job;
  const shown = job ?? last.current;
  return (
    <Sheet open={!!job} onOpenChange={(o) => !o && onClose()}>
      {shown && <DetailBody key={shown.id} job={shown} onAct={onAct ?? (() => {})} onApplied={onApplied} onLeave={onClose} handoff={!!onAct} mark={mark} short={short} />}
    </Sheet>
  );
}

/**
 * The whole job: header, chips, pay, why it fits, facts, description, and a floating Pass / Save / Apply dock.
 * It records decisions itself and then tells `onAct` what was pressed, unless `handoff` is set, in which case it only
 * calls `onAct` and leaves recording to the caller. Passing `mark` makes it controlled: the caller records and says
 * which decision the job holds. An apply Headcount sends is the exception in every mode: the body records it and its
 * undo, and tells `onApplied`. `onClose` adds a close button to the header. A failed application swaps the apply
 * button for its way forward: answering in the Needs you sheet (after `onLeave` closes the sheet holding this), finishing
 * on the company site, or trying again.
 */
export function DetailBody({
  job,
  onAct,
  onApplied,
  onClose,
  onLeave,
  handoff = false,
  mark: held,
  short = false,
  className,
}: {
  job: Job;
  onAct: (d: Decision, j: Job) => void;
  onApplied?: (j: Job, on: boolean) => void;
  onClose?: () => void;
  onLeave?: () => void;
  handoff?: boolean;
  mark?: Decision | null;
  short?: boolean;
  className?: string;
}) {
  const ctx = useApp();
  const decideJob = useDecide();
  const [full, setFull] = useState<JobDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const [own, setMark] = useState<Decision | null>(null);
  const controlled = held !== undefined;
  const mark = controlled ? held : own;
  const [busy, setBusy] = useState(false);
  const applyRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const c = new AbortController();
    setFull(null);
    setFailed(false);
    getJob(job.id, { signal: c.signal }).then(
      (d) => {
        setFull(d);
        setMark(d.decision);
      },
      (e: Error) => e.name !== "AbortError" && setFailed(true),
    );
    return () => c.abort();
  }, [job.id]);

  const act = (d: Decision) => {
    if (handoff || controlled) return onAct(d, job);
    if (busy) return;
    setBusy(true);
    const before = mark;
    setMark(before === d ? null : d);
    decideJob(job, d, before, applyRef.current).then((m) => {
      setMark(m);
      setBusy(false);
      onAct(d, job);
    });
  };

  /** The SendButton's request; the check waits on its answer. */
  const send = (from: Element) => decideJob(job, "apply", mark, from).then((m) => m === "apply");

  const settle = (on: boolean) => {
    if (!controlled) setMark(on ? "apply" : null);
    onApplied?.(job, on);
    if (!handoff && !controlled) onAct("apply", job);
  };

  const unsend = () => {
    if (busy) return;
    setBusy(true);
    decideJob(job, "apply", "apply", applyRef.current).then((m) => {
      setBusy(false);
      if (m !== "apply") settle(false);
    });
  };

  const again = () => {
    if (busy) return;
    setBusy(true);
    retry(job.id).then(
      (r) => {
        setBusy(false);
        setFull((f) => f && { ...f, application: r.application });
        ctx.refreshStats();
        changed();
      },
      (e) => {
        setBusy(false);
        ctx.toast(errorText(e, "Retry didn't go through."), "bad");
      },
    );
  };

  const answerIt = () => {
    onLeave?.();
    ctx.showNeeds();
  };

  const status = full?.application?.status;
  const locked = status ? SENT_LABEL[status] : undefined;
  const sending = mark === "apply" && job.canAutoApply;
  const saved = mark === "save";
  const passed = mark === "pass";
  const money = pay(job);
  const visa = visaNote(job.visa);
  const posting = webUrl(job.jobUrl);
  const site = webUrl(job.applyUrl) ?? posting;

  const app = full?.application ?? null;
  const ghost = ghostRisk(job.ghost);
  const tracked = !!app && app.status !== "skipped";

  return (
    <motion.div variants={staggerParent(0.04)} initial="hidden" animate="show" className={cn("relative flex min-h-full flex-col gap-6", className)}>
      <motion.header variants={rise} className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Monogram name={job.company} url={job.jobUrl} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-bold text-ink">{job.company}</p>
            <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[13px] text-muted">
              {job.postedAt && <span className="shrink-0">Posted {ago(job.postedAt)}</span>}
              {job.postedAt && <span aria-hidden>·</span>}
              <SourceLogo source={job.source} size="xs" withName className="gap-1.5 text-[13px] font-medium text-muted" />
            </p>
          </div>
          {posting && (
            <a
              href={posting}
              target="_blank"
              rel="noreferrer"
              className="hidden h-8 shrink-0 items-center gap-1.5 rounded-control px-2.5 text-[13px] font-semibold text-ink-soft ring-1 ring-line-strong transition-[background-color] duration-150 hover:bg-sunk sm:inline-flex"
            >
              Apply on site <ArrowSquareOut size={14} weight="bold" aria-hidden />
            </a>
          )}
          {onClose && (
            <IconButton label="Close" onClick={onClose} className="-mr-2 self-start text-muted">
              <X size={20} weight="bold" aria-hidden />
            </IconButton>
          )}
        </div>
        <h2 className="font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.03em] text-balance text-ink md:text-[30px]">{job.title}</h2>
        <div className="flex flex-wrap items-center gap-1.5 empty:hidden">
          {app && app.status !== "skipped" ? <AppStatusBadge status={app.status} /> : null}
          {job.canAutoApply && tracked && <StatusBadge tone="accent">Auto-apply</StatusBadge>}
          <MatchChip score={job.score} />
          {visa && <StatusBadge tone={visa.tone === "ok" ? "mint" : "gray"}>{visa.text}</StatusBadge>}
          {ghost?.label === "High" && <StatusBadge tone="red">Ghost risk</StatusBadge>}
          {job.closed && <StatusBadge tone="gray">Closed</StatusBadge>}
        </div>
        <AnimatePresence initial={false}>
          {(full || failed) && !tracked && (
            <motion.div
              initial={{ height: 0, opacity: 0, marginTop: -16 }}
              animate={{ height: "auto", opacity: 1, marginTop: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <Sender job={job} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <motion.div variants={rise} className="overflow-hidden rounded-card shadow-card">
        {money && (
          <div className="flex items-end justify-between gap-3 border-b border-line bg-cream-soft px-4 py-3.5">
            <div>
              <p className="text-[12.5px] font-semibold text-cream-ink">Pay</p>
              <p className="font-display text-[24px] font-extrabold leading-tight tracking-[-0.02em] text-ink tabular-nums">{money}</p>
            </div>
            {job.salaryPeriod === "year" && <p className="pb-1 text-[13px] text-cream-ink">a year</p>}
          </div>
        )}
        <dl className="-mb-px -mr-px grid grid-cols-2 sm:grid-cols-3">
          <Facts job={job} full={full} />
        </dl>
      </motion.div>

      {tracked && app && (
        <motion.section variants={rise} className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-[17px] font-bold tracking-[-0.015em] text-ink">Your application</h3>
            {app.attempts > 1 && <span className="text-[13px] text-muted tabular-nums">{app.attempts} attempts</span>}
          </div>
          <Timeline app={app} />
        </motion.section>
      )}

      <motion.div variants={rise} className="flex flex-col empty:hidden">
        <FitPanel jobId={job.id} auto />
      </motion.div>

      <motion.section variants={rise} className="flex flex-col gap-3">
        <h3 className="text-[17px] font-bold tracking-[-0.015em] text-ink">About the role</h3>
        <Description job={job} full={full} failed={failed} short={short} />
        {posting && (
          <a
            href={posting}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-1.5 self-start text-[15px] font-semibold text-primary-ink underline-offset-4 hover:underline"
          >
            View the posting <ArrowSquareOut size={16} weight="bold" aria-hidden />
          </a>
        )}
      </motion.section>

      <div className="sticky -bottom-5 z-10 -mx-5 -mb-5 mt-auto border-t border-line bg-surface px-5 pb-8 pt-3">
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 380, damping: 26, delay: 0.1 }}
          className="flex items-center gap-2"
        >
          <IconButton
            label={passed ? "Passed" : "Pass"}
            variant="secondary"
            aria-pressed={passed}
            onClick={() => act("pass")}
            disabled={!!locked}
            className={cn("text-ink-soft hover:bg-tone-red hover:text-bad-ink", passed && "bg-tone-red text-bad-ink")}
          >
            <X size={20} weight="bold" aria-hidden />
          </IconButton>
          <IconButton
            label={saved ? "Saved" : "Save"}
            variant="secondary"
            aria-pressed={saved}
            onClick={() => act("save")}
            disabled={!!locked}
            className={cn("text-ink-soft hover:bg-primary-soft hover:text-primary-ink", saved && "bg-primary-soft text-primary")}
          >
            <AnimatePresence initial={false} mode="popLayout">
              <motion.span key={saved ? "on" : "off"} {...swap} className="inline-flex">
                <BookmarkSimple size={20} weight={saved ? "fill" : "bold"} aria-hidden />
              </motion.span>
            </AnimatePresence>
          </IconButton>
          {locked ? (
            <Button variant="secondary" className="flex-1" disabled icon={<Check weight="bold" />}>
              {locked}
            </Button>
          ) : status === "failed" && app?.needs === "answers" ? (
            <Button className="flex-1" onClick={answerIt}>
              Answer and send
            </Button>
          ) : status === "failed" && app?.needs === "site" && site ? (
            <Button className="flex-1" onClick={() => open(site, "_blank", "noopener,noreferrer")} icon={<ArrowSquareOut weight="bold" />}>
              Finish on site
            </Button>
          ) : status === "failed" ? (
            <Button variant="secondary" className="flex-1" loading={busy} onClick={again}>
              Try again
            </Button>
          ) : sending ? (
            <Button ref={applyRef} variant="secondary" className="flex-1 text-tone-amber-ink" aria-pressed loading={busy} icon={<ToneDot />} onClick={unsend}>
              Queued
            </Button>
          ) : job.canAutoApply ? (
            <SendButton className="flex-1" send={send} onSent={() => settle(true)} />
          ) : (
            <Button ref={applyRef} className="flex-1" onClick={() => act("apply")}>
              {mark === "apply" ? "Applied on site" : applyLabel(job)}
            </Button>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

/** Who sends this one, under the title so it reads before anything else. */
function Sender({ job }: { job: Job }) {
  if (!job.canAutoApply)
    return <p className="rounded-card bg-sunk px-3.5 py-2.5 text-[13px] text-ink-soft">You apply to this one on the company site.</p>;
  return (
    <div className="rounded-card bg-tone-accent px-3.5 py-2.5">
      <p className="text-sm font-bold leading-6 text-tone-accent-ink">
        <span className="mr-2 inline-flex h-5 items-center rounded-chip bg-accent px-1.5 align-[1px] text-[11.5px] leading-none text-on-accent">Auto-apply</span>
        Headcount can send this one for you.
      </p>
    </div>
  );
}

type Stop = { label: string; at?: string | null; state: "done" | "now" | "todo" | "bad"; note?: string };

function stops(a: AppView): Stop[] {
  if (a.status === "on_site") return [{ label: "Applied on the company site", at: a.createdAt, state: "done" }];
  const sent = a.status === "submitted" || a.status === "confirmed";
  const out: Stop[] = [{ label: "You applied", at: a.createdAt, state: "done" }];
  if (a.status === "failed") {
    out.push({ label: "Didn't go through", at: a.updatedAt, state: "bad", note: a.reasonLabel || undefined });
    return out;
  }
  out.push({
    label: sent ? "Sent" : a.status === "applying" ? "Sending" : "Queued",
    at: sent ? (a.submittedAt ?? a.updatedAt) : a.status === "applying" ? a.updatedAt : null,
    state: sent ? "done" : "now",
  });
  out.push({ label: "Confirmed by the company", at: a.status === "confirmed" ? a.updatedAt : null, state: a.status === "confirmed" ? "done" : "todo" });
  return out;
}

const when = (iso: string) =>
  new Date(utc(iso)).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function Timeline({ app }: { app: AppView }) {
  const list = stops(app);
  return (
    <ol className="flex flex-col rounded-card px-4 py-3.5 shadow-card">
      {list.map((s, i) => (
        <li key={s.label} className={cn("relative flex gap-3", i < list.length - 1 && "pb-3.5")}>
          {i < list.length - 1 && (
            <span aria-hidden className={cn("absolute left-[7px] top-5 bottom-0 w-0.5", s.state === "done" ? "bg-ok/50" : "bg-ink/10")} />
          )}
          <span
            aria-hidden
            className={cn(
              "relative mt-[3px] grid size-4 shrink-0 place-items-center rounded-full",
              s.state === "done" && "bg-ok text-white",
              s.state === "now" && "bg-tone-amber text-tone-amber-ink ring-1 ring-tone-amber-ink/30",
              s.state === "bad" && "bg-bad text-white",
              s.state === "todo" && "bg-surface ring-2 ring-ink/12",
            )}
          >
            {s.state === "done" ? (
              <Check size={11} weight="bold" />
            ) : s.state === "bad" ? (
              <X size={11} weight="bold" />
            ) : s.state === "now" ? (
              <span className="pulse-dot size-1.5 rounded-full bg-current" />
            ) : null}
          </span>
          <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3">
            <div className="min-w-0">
              <p className={cn("text-sm", s.state === "todo" ? "text-muted" : "font-semibold text-ink", s.state === "bad" && "text-bad-ink")}>{s.label}</p>
              {s.note && <p className={cn("text-[13px]", s.state === "bad" ? "text-bad-ink/90" : "text-muted")}>{s.note}</p>}
            </div>
            {s.at && <time className="shrink-0 text-[12.5px] text-muted tabular-nums">{when(s.at)}</time>}
          </div>
        </li>
      ))}
      <span className="sr-only">Status: {APP_STATUS[app.status].label}</span>
    </ol>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 border-b border-r border-line px-4 py-3">
      <dt className="text-[12.5px] text-muted">{label}</dt>
      <dd className="mt-0.5 line-clamp-2 text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}

function Facts({ job, full }: { job: Job; full: JobDetail | null }) {
  const where = places(job);
  const visa = visaNote(job.visa);
  const ghost = ghostRisk(job.ghost);
  return (
    <>
      {!pay(job) && <Fact label="Pay" value="Not listed" />}
      <Fact label="Workplace" value={WORKPLACE[job.workplace] ?? "Not listed"} />
      <Fact label="Level" value={(job.seniority && SENIORITY[job.seniority]) || "Not listed"} />
      <Fact label="Location" value={<span title={where.join("; ")}>{where.length ? list(where, 2) : place(job)}</span>} />
      <Fact label="Field" value={(job.family && FAMILIES[job.family]) || "Not listed"} />
      <Fact label="Type" value={full?.employmentType ?? "Not listed"} />
      <Fact label="Visa" value={visa ? <span className={visa.tone === "ok" ? "text-ok-ink" : "text-bad-ink"}>{visa.text}</span> : "Not stated"} />
      {full?.department && <Fact label="Department" value={full.department} />}
      {full?.team && <Fact label="Team" value={full.team} />}
      <Fact label="Ghost risk" value={ghost ? <span className={cn(ghost.tone === "red" && "text-bad-ink", ghost.tone === "amber" && "text-warn-ink")}>{ghost.label}</span> : "Unknown"} />
    </>
  );
}

function Description({ job, full, failed, short }: { job: Job; full: JobDetail | null; failed: boolean; short: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const [whole, setWhole] = useState(false);
  const [cut, setCut] = useState(false);
  const text = full?.descriptionText;
  useEffect(() => {
    const el = box.current;
    if (el) setCut(el.scrollHeight > el.clientHeight + 1);
  }, [text, short]);
  if (!full && !failed) return <Loading row label="Loading the description" />;
  if (!text) return <p className="text-[15px] text-muted">{failed ? "The description didn't load." : (job.descriptionSnippet ?? "No description on file.")}</p>;
  const clamped = short && !whole;
  return (
    <div className="flex flex-col items-start gap-1">
      <div
        ref={box}
        className={cn(
          "flex max-w-[65ch] flex-col gap-3 text-[15px] leading-[1.7] text-ink-soft",
          clamped && "max-h-[10.25rem] overflow-hidden",
          clamped && cut && "[mask-image:linear-gradient(to_bottom,black_55%,transparent)]",
        )}
      >
        {blocks(text, job.title).map((b, i) =>
          b.kind === "ul" ? (
            <ul key={i} className="flex flex-col gap-2 pl-1">
              {b.items.map((x, k) => (
                <li key={k} className="flex gap-3 text-pretty">
                  <span aria-hidden className="mt-[0.7em] size-1.5 shrink-0 rounded-xs bg-primary" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          ) : b.kind === "h" ? (
            <h4 key={i} className="mt-3 font-bold text-ink text-balance">
              {b.text}
            </h4>
          ) : (
            <p key={i} className="text-pretty">
              {b.text}
            </p>
          ),
        )}
      </div>
      {short && (cut || whole) && (
        <button
          type="button"
          aria-expanded={whole}
          onClick={() => setWhole((w) => !w)}
          className="min-h-11 text-[15px] font-semibold text-primary-ink underline-offset-4 hover:underline"
        >
          {whole ? "Show less" : "Show full"}
        </button>
      )}
    </div>
  );
}
